import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { blendingBatchesApi, roastingBatchesApi, suppliersApi, suppliesApi, warehousesApi } from '../../../services/api'
import StatusBadge from './StatusBadge'
import TableActionButton from '../../../components/TableActionButton'
import SortableTh from '../../../components/SortableTh'
import { useSortableData } from '../../../hooks/useSortableData'

interface Warehouse {
  id: number
  name: string
  is_active: boolean
}

interface Supply {
  id: number
  name: string
  current_cost_per_kg: number
}

interface Supplier {
  id: number
  name: string
  is_active: boolean
}

interface RoastingBatch {
  id: number
  batch_code: string
  kg_remaining: number | null
  cost_per_kg: number | null
  status: string
}

interface BlendingBatch {
  id: number
  batch_code: string
  source_roasting_batch_id: number | null
  source_batch_code: string | null
  kg_roasted_coffee_used: number
  roasting_cost_allocated: number
  kg_wheat: number
  wheat_cost_per_kg: number
  kg_sugar: number
  sugar_cost_per_kg: number
  kg_barley: number
  // Null on legacy batches created before cebada existed (barley_supply_id
  // was never snapshotted for them) -- unlike kg_barley, which always has
  // a value (NOT NULL DEFAULT 0).
  barley_cost_per_kg: number | null
  blending_cost: number
  product_cost: number | null
  kg_output: number | null
  cost_per_kg: number | null
  kg_remaining: number | null
  status: string
  warehouse_id: number
  warehouse_name: string
  requested_date: string | null
  completed_date: string | null
  notes: string | null
  is_active: boolean
  supplier_id: number | null
  supplier_name: string | null
  direct_price_per_kg: number | null
  direct_assembly_cost: number | null
  direct_freight_cost: number | null
  direct_storage_cost: number | null
}

interface CreateForm {
  source_roasting_batch_id: string
  kg_roasted_coffee_used: string
  kg_wheat: string
  wheat_cost_per_kg: string
  kg_sugar: string
  sugar_cost_per_kg: string
  kg_barley: string
  barley_cost_per_kg: string
  warehouse_id: string
  notes: string
}

const emptyCreateForm: CreateForm = {
  source_roasting_batch_id: '',
  kg_roasted_coffee_used: '',
  kg_wheat: '',
  wheat_cost_per_kg: '',
  kg_sugar: '',
  sugar_cost_per_kg: '',
  kg_barley: '',
  barley_cost_per_kg: '',
  warehouse_id: '',
  notes: '',
}

interface DirectForm {
  kg_purchased: string
  price_per_kg: string
  assembly_cost: string
  freight_cost: string
  storage_cost: string
  supplier_id: string
  warehouse_id: string
  status: 'at_rest' | 'in_transit'
  origin_warehouse_id: string
  transit_eta: string
  notes: string
}

const emptyDirectForm: DirectForm = {
  kg_purchased: '',
  price_per_kg: '',
  assembly_cost: '',
  freight_cost: '',
  storage_cost: '',
  supplier_id: '',
  warehouse_id: '',
  status: 'at_rest',
  origin_warehouse_id: '',
  transit_eta: '',
  notes: '',
}

interface EditForm {
  warehouse_id: string
  notes: string
  origin_warehouse_id: string
  transit_eta: string
  status: 'at_rest' | 'in_transit'
  kg_roasted_coffee_used: string
  kg_wheat: string
  kg_sugar: string
  kg_barley: string
  price_per_kg: string
  assembly_cost: string
  freight_cost: string
  storage_cost: string
}

function findSupplyCost(supplies: Supply[], name: string): string {
  const s = supplies.find((sup) => sup.name.toLowerCase() === name.toLowerCase())
  return s ? String(s.current_cost_per_kg) : ''
}

export default function BlendingTab() {
  const [batches, setBatches] = useState<BlendingBatch[]>([])
  const [roastingBatches, setRoastingBatches] = useState<RoastingBatch[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [supplies, setSupplies] = useState<Supply[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const [showForm, setShowForm] = useState(false)
  const [createMode, setCreateMode] = useState<'chain' | 'direct'>('chain')
  const [form, setForm] = useState<CreateForm>(emptyCreateForm)
  const [directForm, setDirectForm] = useState<DirectForm>(emptyDirectForm)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const [editingBatch, setEditingBatch] = useState<BlendingBatch | null>(null)
  const [editForm, setEditForm] = useState<EditForm>({
    warehouse_id: '',
    notes: '',
    origin_warehouse_id: '',
    transit_eta: '',
    status: 'at_rest',
    kg_roasted_coffee_used: '',
    kg_wheat: '',
    kg_sugar: '',
    kg_barley: '',
    price_per_kg: '',
    assembly_cost: '',
    freight_cost: '',
    storage_cost: '',
  })
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState('')
  const [completingId, setCompletingId] = useState<number | null>(null)
  const [cancelingId, setCancelingId] = useState<number | null>(null)

  const fetchAll = async () => {
    try {
      setLoadError('')
      const [batchesRes, roastRes, whRes, suppliesRes, suppliersRes] = await Promise.all([
        blendingBatchesApi.list(),
        roastingBatchesApi.list({ status: 'at_rest' }),
        warehousesApi.list(),
        suppliesApi.list(),
        suppliersApi.list(),
      ])
      setBatches(batchesRes.data.batches || [])
      setRoastingBatches((roastRes.data.batches || []).filter((b: RoastingBatch) => (b.kg_remaining || 0) > 0))
      setWarehouses(whRes.data.warehouses || [])
      setSupplies(suppliesRes.data.supplies || [])
      setSuppliers(suppliersRes.data.suppliers || [])
    } catch (err) {
      console.error('Error fetching blending batches:', err)
      setLoadError('No se pudieron cargar los lotes de mezclado. Intenta recargar la página.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  const activeWarehouses = useMemo(() => warehouses.filter((w) => w.is_active), [warehouses])

  const filteredBatches = useMemo(() => {
    if (!statusFilter) return batches
    return batches.filter((b) => b.status === statusFilter)
  }, [batches, statusFilter])

  const { sortedData: sortedBatches, sortConfig, requestSort } = useSortableData(filteredBatches)

  const selectedSource = useMemo(
    () => roastingBatches.find((b) => String(b.id) === form.source_roasting_batch_id),
    [roastingBatches, form.source_roasting_batch_id]
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const openNew = () => {
    setCreateMode('chain')
    setForm({
      ...emptyCreateForm,
      wheat_cost_per_kg: findSupplyCost(supplies, 'Trigo'),
      sugar_cost_per_kg: findSupplyCost(supplies, 'Azúcar'),
      barley_cost_per_kg: findSupplyCost(supplies, 'Cebada'),
    })
    setDirectForm(emptyDirectForm)
    setError('')
    setShowForm(true)
  }

  const handleDirectChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setDirectForm({ ...directForm, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (createMode === 'direct') {
      const kg = parseFloat(directForm.kg_purchased)
      if (!kg || kg <= 0) {
        setError('Ingresa un peso comprado válido')
        return
      }
      if (!directForm.warehouse_id) {
        setError('Selecciona un almacén')
        return
      }
      if (directForm.status === 'in_transit' && !directForm.origin_warehouse_id) {
        setError('Selecciona el almacén de origen para una compra en tránsito')
        return
      }
      setSaving(true)
      setError('')
      try {
        await blendingBatchesApi.create({
          kg_purchased: kg,
          price_per_kg: directForm.price_per_kg ? parseFloat(directForm.price_per_kg) : undefined,
          assembly_cost: directForm.assembly_cost ? parseFloat(directForm.assembly_cost) : undefined,
          freight_cost: directForm.freight_cost ? parseFloat(directForm.freight_cost) : undefined,
          storage_cost: directForm.storage_cost ? parseFloat(directForm.storage_cost) : undefined,
          supplier_id: directForm.supplier_id ? Number(directForm.supplier_id) : undefined,
          warehouse_id: Number(directForm.warehouse_id),
          status: directForm.status,
          origin_warehouse_id:
            directForm.status === 'in_transit' && directForm.origin_warehouse_id
              ? Number(directForm.origin_warehouse_id)
              : undefined,
          transit_eta: directForm.status === 'in_transit' && directForm.transit_eta ? directForm.transit_eta : undefined,
          notes: directForm.notes || undefined,
        })
        toast.success('Compra directa de mezclado registrada')
        setShowForm(false)
        fetchAll()
      } catch (err: any) {
        setError(err.response?.data?.error || 'Error al registrar la compra directa')
      } finally {
        setSaving(false)
      }
      return
    }
    if (!form.source_roasting_batch_id) {
      setError('Selecciona el lote de tostado de origen')
      return
    }
    const kgCoffee = parseFloat(form.kg_roasted_coffee_used)
    if (!kgCoffee || kgCoffee <= 0) {
      setError('Ingresa un peso de café tostado válido')
      return
    }
    if (selectedSource && kgCoffee > Number(selectedSource.kg_remaining)) {
      setError(`Solo quedan ${Number(selectedSource.kg_remaining).toFixed(2)} kg disponibles en este lote`)
      return
    }
    if (form.kg_wheat === '' || parseFloat(form.kg_wheat) < 0) {
      setError('Ingresa un peso de trigo válido')
      return
    }
    if (form.kg_sugar === '' || parseFloat(form.kg_sugar) < 0) {
      setError('Ingresa un peso de azúcar válido')
      return
    }
    if (form.kg_barley === '' || parseFloat(form.kg_barley) < 0) {
      setError('Ingresa un peso de cebada válido')
      return
    }
    if (!form.warehouse_id) {
      setError('Selecciona un almacén')
      return
    }
    setSaving(true)
    setError('')
    try {
      await blendingBatchesApi.create({
        source_roasting_batch_id: Number(form.source_roasting_batch_id),
        kg_roasted_coffee_used: kgCoffee,
        kg_wheat: parseFloat(form.kg_wheat),
        wheat_cost_per_kg: form.wheat_cost_per_kg ? parseFloat(form.wheat_cost_per_kg) : undefined,
        kg_sugar: parseFloat(form.kg_sugar),
        sugar_cost_per_kg: form.sugar_cost_per_kg ? parseFloat(form.sugar_cost_per_kg) : undefined,
        kg_barley: parseFloat(form.kg_barley),
        barley_cost_per_kg: form.barley_cost_per_kg ? parseFloat(form.barley_cost_per_kg) : undefined,
        warehouse_id: Number(form.warehouse_id),
        notes: form.notes || undefined,
      })
      toast.success('Lote de mezclado solicitado')
      setShowForm(false)
      fetchAll()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al crear el lote de mezclado')
    } finally {
      setSaving(false)
    }
  }

  const openEdit = (batch: BlendingBatch) => {
    setEditingBatch(batch)
    setEditForm({
      warehouse_id: String(batch.warehouse_id),
      notes: batch.notes || '',
      origin_warehouse_id: '',
      transit_eta: '',
      status: batch.status === 'in_transit' ? 'in_transit' : 'at_rest',
      kg_roasted_coffee_used: String(batch.kg_roasted_coffee_used),
      kg_wheat: String(batch.kg_wheat),
      kg_sugar: String(batch.kg_sugar),
      kg_barley: String(batch.kg_barley),
      price_per_kg: batch.direct_price_per_kg !== null ? String(batch.direct_price_per_kg) : '',
      assembly_cost: batch.direct_assembly_cost !== null ? String(batch.direct_assembly_cost) : '',
      freight_cost: batch.direct_freight_cost !== null ? String(batch.direct_freight_cost) : '',
      storage_cost: batch.direct_storage_cost !== null ? String(batch.direct_storage_cost) : '',
    })
    setEditError('')
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingBatch) return
    const isPending = editingBatch.status === 'pending'
    const isDirect = editingBatch.source_roasting_batch_id === null
    const kgCoffee = parseFloat(editForm.kg_roasted_coffee_used)
    if (!kgCoffee || kgCoffee <= 0) {
      setEditError(isDirect ? 'Ingresa un peso comprado válido' : 'Ingresa un peso de café tostado válido')
      return
    }
    setEditSaving(true)
    setEditError('')
    try {
      const payload: any = { warehouse_id: Number(editForm.warehouse_id), notes: editForm.notes || null }
      if (!isPending) {
        payload.origin_warehouse_id = editForm.origin_warehouse_id ? Number(editForm.origin_warehouse_id) : null
        payload.transit_eta = editForm.transit_eta || null
        payload.status = editForm.status
      }
      if (isDirect) {
        payload.kg_roasted_coffee_used = kgCoffee
        payload.direct_price_per_kg = parseFloat(editForm.price_per_kg) || 0
        payload.direct_assembly_cost = parseFloat(editForm.assembly_cost) || 0
        payload.direct_freight_cost = parseFloat(editForm.freight_cost) || 0
        payload.direct_storage_cost = parseFloat(editForm.storage_cost) || 0
      } else {
        payload.kg_roasted_coffee_used = kgCoffee
        payload.kg_wheat = parseFloat(editForm.kg_wheat) || 0
        payload.kg_sugar = parseFloat(editForm.kg_sugar) || 0
        payload.kg_barley = parseFloat(editForm.kg_barley) || 0
      }
      await blendingBatchesApi.update(editingBatch.id, payload)
      toast.success('Lote de mezclado actualizado')
      setEditingBatch(null)
      fetchAll()
    } catch (err: any) {
      setEditError(err.response?.data?.error || 'Error al actualizar el lote')
    } finally {
      setEditSaving(false)
    }
  }

  const handleComplete = async (batch: BlendingBatch) => {
    if (!confirm(`¿Completar el mezclado ${batch.batch_code}? Esto calculará el kg de salida final.`)) return
    setCompletingId(batch.id)
    try {
      await blendingBatchesApi.complete(batch.id, {})
      toast.success('Mezclado completado')
      fetchAll()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al completar el mezclado')
    } finally {
      setCompletingId(null)
    }
  }

  const canCancel = (batch: BlendingBatch) =>
    batch.status === 'pending' ||
    (batch.status !== 'consumed' && Number(batch.kg_remaining) === Number(batch.kg_output))

  const handleCancel = async (batch: BlendingBatch) => {
    if (!confirm(`¿Anular el lote de mezclado ${batch.batch_code}? Esta acción restaurará los kilos reservados en la etapa anterior.`)) return
    setCancelingId(batch.id)
    try {
      await blendingBatchesApi.cancel(batch.id)
      toast.success('Lote de mezclado anulado')
      fetchAll()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al anular el lote')
    } finally {
      setCancelingId(null)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="flex items-center gap-3">
          <label htmlFor="blending-status-filter" className="text-xs font-medium text-espresso-500">
            Estado
          </label>
          <select
            id="blending-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
          >
            <option value="">Todos</option>
            <option value="pending">Pendiente</option>
            <option value="in_transit">En Tránsito</option>
            <option value="at_rest">En Almacén</option>
            <option value="consumed">Consumido</option>
          </select>
        </div>
        <button
          onClick={openNew}
          disabled={roastingBatches.length === 0 || activeWarehouses.length === 0}
          title={
            roastingBatches.length === 0
              ? 'No hay lotes de tostado en almacén disponibles para mezclar'
              : activeWarehouses.length === 0
              ? 'Crea primero un almacén activo'
              : undefined
          }
          className="px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
        >
          + Nuevo Mezclado
        </button>
      </div>

      {roastingBatches.length === 0 && (
        <div className="bg-amber-50 text-amber-800 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          No hay lotes de tostado en almacén con kg disponibles. Completa un tostado primero.
        </div>
      )}

      {loadError && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          {loadError}
        </div>
      )}

      {!loadError && filteredBatches.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">
            {batches.length === 0 ? 'No hay lotes de mezclado registrados aún.' : 'No hay lotes con este estado.'}
          </p>
        </div>
      ) : (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Lote" sortKey="batch_code" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Fecha" sortKey="requested_date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Origen" sortKey="source_batch_code" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Kg Café" sortKey="kg_roasted_coffee_used" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Kg Trigo" sortKey="kg_wheat" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Kg Azúcar" sortKey="kg_sugar" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Kg Cebada" sortKey="kg_barley" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Kg Salida" sortKey="kg_output" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Costo/kg" sortKey="cost_per_kg" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedBatches.map((b) => (
                  <tr key={b.id} className="hover:bg-cream-100">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{b.batch_code}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {b.requested_date ? new Date(b.requested_date).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {b.source_batch_code || `Compra directa${b.supplier_name ? ` — ${b.supplier_name}` : ''}`}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{Number(b.kg_roasted_coffee_used).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{Number(b.kg_wheat).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{Number(b.kg_sugar).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{Number(b.kg_barley).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {b.kg_output !== null ? Number(b.kg_output).toFixed(2) : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {b.cost_per_kg !== null ? `$${Number(b.cost_per_kg).toFixed(2)}` : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap"><StatusBadge status={b.status} isActive={b.is_active} /></td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-1">
                        {b.status !== 'consumed' && (
                          <TableActionButton onClick={() => openEdit(b)} variant="primary">
                            Editar
                          </TableActionButton>
                        )}
                        {b.status === 'pending' && (
                          <TableActionButton onClick={() => handleComplete(b)} variant="success" disabled={completingId === b.id}>
                            {completingId === b.id ? 'Completando...' : 'Completar'}
                          </TableActionButton>
                        )}
                        {b.is_active !== false && (
                          <TableActionButton
                            onClick={() => handleCancel(b)}
                            variant="danger"
                            disabled={cancelingId === b.id || !canCancel(b)}
                            title={!canCancel(b) ? 'No se puede anular: ya se generaron lotes de empaquetado a partir de este lote' : undefined}
                          >
                            {cancelingId === b.id ? 'Anulando...' : 'Anular'}
                          </TableActionButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Nuevo Lote de Mezclado</h2>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm" role="alert">
                  {error}
                </div>
              )}

              <div className="flex rounded-md overflow-hidden border border-espresso-300 w-fit">
                <button
                  type="button"
                  onClick={() => setCreateMode('chain')}
                  className={`px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 ${
                    createMode === 'chain' ? 'bg-terracotta-600 text-white' : 'bg-cream-50 text-espresso-700 hover:bg-cream-100'
                  }`}
                >
                  Producido internamente
                </button>
                <button
                  type="button"
                  onClick={() => setCreateMode('direct')}
                  className={`px-4 py-2 text-sm font-medium border-l border-espresso-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 ${
                    createMode === 'direct' ? 'bg-terracotta-600 text-white' : 'bg-cream-50 text-espresso-700 hover:bg-cream-100'
                  }`}
                >
                  Compra directa
                </button>
              </div>

              {createMode === 'chain' && (
                <>
                  <div>
                    <label htmlFor="blend-source" className="block text-sm font-medium text-espresso-700 mb-1">
                      Lote de tostado de origen *
                    </label>
                    <select
                      id="blend-source"
                      name="source_roasting_batch_id"
                      value={form.source_roasting_batch_id}
                      onChange={handleChange}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      required
                    >
                      <option value="">Selecciona un lote</option>
                      {roastingBatches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.batch_code} · {Number(b.kg_remaining).toFixed(2)} kg disponibles
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="blend-kg-coffee" className="block text-sm font-medium text-espresso-700 mb-1">
                      Kg de café tostado a usar *
                    </label>
                    <input
                      id="blend-kg-coffee"
                      type="number"
                      name="kg_roasted_coffee_used"
                      value={form.kg_roasted_coffee_used}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="blend-kg-wheat" className="block text-sm font-medium text-espresso-700 mb-1">
                        Kg de trigo *
                      </label>
                      <input
                        id="blend-kg-wheat"
                        type="number"
                        name="kg_wheat"
                        value={form.kg_wheat}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="blend-wheat-cost" className="block text-sm font-medium text-espresso-700 mb-1">
                        Costo/kg trigo
                      </label>
                      <input
                        id="blend-wheat-cost"
                        type="number"
                        name="wheat_cost_per_kg"
                        value={form.wheat_cost_per_kg}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        placeholder="Usa costo actual del insumo"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="blend-kg-sugar" className="block text-sm font-medium text-espresso-700 mb-1">
                        Kg de azúcar *
                      </label>
                      <input
                        id="blend-kg-sugar"
                        type="number"
                        name="kg_sugar"
                        value={form.kg_sugar}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="blend-sugar-cost" className="block text-sm font-medium text-espresso-700 mb-1">
                        Costo/kg azúcar
                      </label>
                      <input
                        id="blend-sugar-cost"
                        type="number"
                        name="sugar_cost_per_kg"
                        value={form.sugar_cost_per_kg}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        placeholder="Usa costo actual del insumo"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="blend-kg-barley" className="block text-sm font-medium text-espresso-700 mb-1">
                        Kg de cebada *
                      </label>
                      <input
                        id="blend-kg-barley"
                        type="number"
                        name="kg_barley"
                        value={form.kg_barley}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="blend-barley-cost" className="block text-sm font-medium text-espresso-700 mb-1">
                        Costo/kg cebada
                      </label>
                      <input
                        id="blend-barley-cost"
                        type="number"
                        name="barley_cost_per_kg"
                        value={form.barley_cost_per_kg}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        placeholder="Usa costo actual del insumo"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                  </div>
                </>
              )}

              {createMode === 'direct' && (
                <>
                  <div>
                    <label htmlFor="blend-direct-kg" className="block text-sm font-medium text-espresso-700 mb-1">
                      Kg Comprados *
                    </label>
                    <input
                      id="blend-direct-kg"
                      type="number"
                      name="kg_purchased"
                      value={directForm.kg_purchased}
                      onChange={handleDirectChange}
                      step="0.01"
                      min="0"
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="blend-direct-price" className="block text-sm font-medium text-espresso-700 mb-1">
                      Precio / kg
                    </label>
                    <input
                      id="blend-direct-price"
                      type="number"
                      name="price_per_kg"
                      value={directForm.price_per_kg}
                      onChange={handleDirectChange}
                      step="0.01"
                      min="0"
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="blend-direct-assembly" className="block text-sm font-medium text-espresso-700 mb-1">
                        Acopio
                      </label>
                      <input
                        id="blend-direct-assembly"
                        type="number"
                        name="assembly_cost"
                        value={directForm.assembly_cost}
                        onChange={handleDirectChange}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="blend-direct-freight" className="block text-sm font-medium text-espresso-700 mb-1">
                        Flete
                      </label>
                      <input
                        id="blend-direct-freight"
                        type="number"
                        name="freight_cost"
                        value={directForm.freight_cost}
                        onChange={handleDirectChange}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="blend-direct-storage" className="block text-sm font-medium text-espresso-700 mb-1">
                        Almacenaje
                      </label>
                      <input
                        id="blend-direct-storage"
                        type="number"
                        name="storage_cost"
                        value={directForm.storage_cost}
                        onChange={handleDirectChange}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="blend-direct-supplier" className="block text-sm font-medium text-espresso-700 mb-1">
                      Proveedor
                    </label>
                    <select
                      id="blend-direct-supplier"
                      name="supplier_id"
                      value={directForm.supplier_id}
                      onChange={handleDirectChange}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    >
                      <option value="">Sin proveedor</option>
                      {suppliers.map((s) => (
                        <option key={s.id} value={s.id} disabled={!s.is_active}>
                          {s.name}{!s.is_active ? ' (inactivo)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="blend-direct-status" className="block text-sm font-medium text-espresso-700 mb-1">
                      Estado
                    </label>
                    <select
                      id="blend-direct-status"
                      name="status"
                      value={directForm.status}
                      onChange={handleDirectChange}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    >
                      <option value="at_rest">En Almacén</option>
                      <option value="in_transit">En Tránsito</option>
                    </select>
                  </div>
                  {directForm.status === 'in_transit' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="blend-direct-origin" className="block text-sm font-medium text-espresso-700 mb-1">
                          Almacén origen
                        </label>
                        <select
                          id="blend-direct-origin"
                          name="origin_warehouse_id"
                          value={directForm.origin_warehouse_id}
                          onChange={handleDirectChange}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        >
                          <option value="">Selecciona</option>
                          {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="blend-direct-eta" className="block text-sm font-medium text-espresso-700 mb-1">
                          ETA
                        </label>
                        <input
                          id="blend-direct-eta"
                          type="date"
                          name="transit_eta"
                          value={directForm.transit_eta}
                          onChange={handleDirectChange}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              <div>
                <label htmlFor="blend-warehouse" className="block text-sm font-medium text-espresso-700 mb-1">
                  Almacén *
                </label>
                <select
                  id="blend-warehouse"
                  name="warehouse_id"
                  value={createMode === 'chain' ? form.warehouse_id : directForm.warehouse_id}
                  onChange={createMode === 'chain' ? handleChange : handleDirectChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                >
                  <option value="">Selecciona un almacén</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id} disabled={!w.is_active}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="blend-notes" className="block text-sm font-medium text-espresso-700 mb-1">
                  Notas
                </label>
                <textarea
                  id="blend-notes"
                  name="notes"
                  value={createMode === 'chain' ? form.notes : directForm.notes}
                  onChange={createMode === 'chain' ? handleChange : handleDirectChange}
                  rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Editar Lote {editingBatch.batch_code}</h2>
            </div>
            <form onSubmit={handleEditSubmit} className="px-6 py-4 space-y-4">
              {editError && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm" role="alert">
                  {editError}
                </div>
              )}
              <div>
                <label htmlFor="edit-blend-warehouse" className="block text-sm font-medium text-espresso-700 mb-1">
                  Almacén
                </label>
                <select
                  id="edit-blend-warehouse"
                  value={editForm.warehouse_id}
                  onChange={(e) => setEditForm({ ...editForm, warehouse_id: e.target.value })}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                >
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </div>
              {editingBatch.source_roasting_batch_id === null ? (
                <>
                  <div>
                    <label htmlFor="edit-blend-kg-purchased" className="block text-sm font-medium text-espresso-700 mb-1">
                      Kg comprados
                    </label>
                    <input
                      id="edit-blend-kg-purchased"
                      type="number"
                      value={editForm.kg_roasted_coffee_used}
                      onChange={(e) => setEditForm({ ...editForm, kg_roasted_coffee_used: e.target.value })}
                      step="0.01"
                      min="0.01"
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="edit-blend-price" className="block text-sm font-medium text-espresso-700 mb-1">
                      Precio / kg
                    </label>
                    <input
                      id="edit-blend-price"
                      type="number"
                      value={editForm.price_per_kg}
                      onChange={(e) => setEditForm({ ...editForm, price_per_kg: e.target.value })}
                      step="0.01"
                      min="0"
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="edit-blend-assembly" className="block text-sm font-medium text-espresso-700 mb-1">
                        Acopio
                      </label>
                      <input
                        id="edit-blend-assembly"
                        type="number"
                        value={editForm.assembly_cost}
                        onChange={(e) => setEditForm({ ...editForm, assembly_cost: e.target.value })}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="edit-blend-freight" className="block text-sm font-medium text-espresso-700 mb-1">
                        Flete
                      </label>
                      <input
                        id="edit-blend-freight"
                        type="number"
                        value={editForm.freight_cost}
                        onChange={(e) => setEditForm({ ...editForm, freight_cost: e.target.value })}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="edit-blend-storage" className="block text-sm font-medium text-espresso-700 mb-1">
                        Almacenaje
                      </label>
                      <input
                        id="edit-blend-storage"
                        type="number"
                        value={editForm.storage_cost}
                        onChange={(e) => setEditForm({ ...editForm, storage_cost: e.target.value })}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label htmlFor="edit-blend-kg-coffee" className="block text-sm font-medium text-espresso-700 mb-1">
                      Kg de café tostado a usar
                    </label>
                    <input
                      id="edit-blend-kg-coffee"
                      type="number"
                      value={editForm.kg_roasted_coffee_used}
                      onChange={(e) => setEditForm({ ...editForm, kg_roasted_coffee_used: e.target.value })}
                      step="0.01"
                      min="0"
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="edit-blend-kg-wheat" className="block text-sm font-medium text-espresso-700 mb-1">
                        Kg de trigo
                      </label>
                      <input
                        id="edit-blend-kg-wheat"
                        type="number"
                        value={editForm.kg_wheat}
                        onChange={(e) => setEditForm({ ...editForm, kg_wheat: e.target.value })}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="edit-blend-kg-sugar" className="block text-sm font-medium text-espresso-700 mb-1">
                        Kg de azúcar
                      </label>
                      <input
                        id="edit-blend-kg-sugar"
                        type="number"
                        value={editForm.kg_sugar}
                        onChange={(e) => setEditForm({ ...editForm, kg_sugar: e.target.value })}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="edit-blend-kg-barley" className="block text-sm font-medium text-espresso-700 mb-1">
                        Kg de cebada
                      </label>
                      <input
                        id="edit-blend-kg-barley"
                        type="number"
                        value={editForm.kg_barley}
                        onChange={(e) => setEditForm({ ...editForm, kg_barley: e.target.value })}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                  </div>
                </>
              )}
              {editingBatch.status !== 'pending' && (
                <>
                  <div>
                    <label htmlFor="edit-blend-status" className="block text-sm font-medium text-espresso-700 mb-1">
                      Estado
                    </label>
                    <select
                      id="edit-blend-status"
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value as 'at_rest' | 'in_transit' })}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    >
                      <option value="at_rest">En Almacén</option>
                      <option value="in_transit">En Tránsito</option>
                    </select>
                  </div>
                  {editForm.status === 'in_transit' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="edit-blend-origin" className="block text-sm font-medium text-espresso-700 mb-1">
                          Almacén origen
                        </label>
                        <select
                          id="edit-blend-origin"
                          value={editForm.origin_warehouse_id}
                          onChange={(e) => setEditForm({ ...editForm, origin_warehouse_id: e.target.value })}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        >
                          <option value="">Selecciona</option>
                          {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="edit-blend-eta" className="block text-sm font-medium text-espresso-700 mb-1">
                          ETA
                        </label>
                        <input
                          id="edit-blend-eta"
                          type="date"
                          value={editForm.transit_eta}
                          onChange={(e) => setEditForm({ ...editForm, transit_eta: e.target.value })}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              <div>
                <label htmlFor="edit-blend-notes" className="block text-sm font-medium text-espresso-700 mb-1">
                  Notas
                </label>
                <textarea
                  id="edit-blend-notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setEditingBatch(null)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={editSaving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  {editSaving ? 'Guardando...' : 'Actualizar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
